/**
 * Premium email marketing templates — block-based engine.
 *
 * Each template = a `theme` (colors/fonts) + a template-specific `hero`.
 * Shared block builders render: logo, banner, body, countdown, events grid,
 * lineup, discount code, dual CTAs and social links — driven purely by the
 * `content` object. The producer never touches HTML.
 */

const safeHtml = (txt = '') =>
  String(txt)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const nl2br = (txt = '') => safeHtml(txt).replace(/\n/g, '<br/>');

const normalizeUrl = (u = '') => {
  const v = String(u).trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith('@')) return `https://instagram.com/${v.slice(1)}`;
  return `https://${v}`;
};

const textOn = (hex) => {
  try {
    const h = String(hex).replace('#', '');
    const full = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#000000' : '#ffffff';
  } catch { return '#000000'; }
};

// ---------------------------------------------------------------------------
// Shared blocks
// ---------------------------------------------------------------------------

const sectionTitle = (label, t) =>
  `<div style="font-size:10px;letter-spacing:3px;text-transform:uppercase;color:${t.accent};font-weight:800;margin:28px 0 12px;">${safeHtml(label)}</div>`;

const logoBlock = (c, t) => {
  if (!c.logo_url) return '';
  return `<tr><td style="padding:28px 32px 0;text-align:center;"><img src="${safeHtml(c.logo_url)}" alt="logo" style="max-height:52px;max-width:220px;display:inline-block;"/></td></tr>`;
};

const bannerBlock = (c) => {
  const img = c.banner_image || c.event_image;
  if (!img) return '';
  return `<tr><td style="padding:0;"><img src="${safeHtml(img)}" alt="${safeHtml(c.title || '')}" style="width:100%;height:260px;object-fit:cover;display:block;"/></td></tr>`;
};

const bodyBlock = (c, t) => {
  let out = '';
  if (c.body1) out += `<p style="margin:0 0 20px;color:${t.text};font-size:15px;line-height:1.7;">${nl2br(c.body1)}</p>`;
  if (c.body2) out += `<p style="margin:0 0 20px;color:${t.sub};font-size:14px;line-height:1.7;">${nl2br(c.body2)}</p>`;
  return out;
};

const countdownBlock = (c, t) => {
  if (!c.countdown_date) return '';
  const target = new Date(c.countdown_date);
  if (isNaN(target)) return '';
  const diff = target - Date.now();
  if (diff <= 0) return '';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const big = days > 0 ? days : hours;
  const unit = days > 0 ? (days === 1 ? 'DÍA' : 'DÍAS') : (hours === 1 ? 'HORA' : 'HORAS');
  const label = c.countdown_label || 'Falta cada vez menos';
  return `
  <div style="margin:24px 0;border:1px solid ${t.accent};border-radius:14px;padding:24px;text-align:center;background:${t.blockBg};">
    <div style="font-size:10px;letter-spacing:3px;text-transform:uppercase;color:${t.sub};margin-bottom:6px;">${safeHtml(label)}</div>
    <div style="font-size:56px;font-weight:900;line-height:1;color:${t.accent};">${big}</div>
    <div style="font-size:13px;letter-spacing:4px;text-transform:uppercase;color:${t.text};font-weight:800;margin-top:4px;">${unit}</div>
  </div>`;
};

const eventsGridBlock = (c, t) => {
  const list = (c.events || []).filter(e => e && e.title).slice(0, 4);
  if (!list.length) return '';
  const cell = (ev) => `<td width="50%" valign="top" style="padding:6px;">
    <a href="${safeHtml(ev.url || '#')}" style="text-decoration:none;color:inherit;">
      <div style="border:1px solid ${t.cardBorder};border-radius:10px;overflow:hidden;background:${t.blockBg};">
        ${ev.image ? `<img src="${safeHtml(ev.image)}" alt="" style="width:100%;height:120px;object-fit:cover;display:block;"/>` : ''}
        <div style="padding:10px 12px;">
          <div style="font-size:13px;font-weight:800;color:${t.text};line-height:1.3;">${safeHtml(ev.title)}</div>
          ${ev.date_label ? `<div style="font-size:11px;color:${t.sub};margin-top:2px;">${safeHtml(ev.date_label)}</div>` : ''}
          <div style="font-size:11px;color:${t.accent};font-weight:800;margin-top:6px;">Ver evento →</div>
        </div>
      </div>
    </a></td>`;
  let rows = '';
  for (let i = 0; i < list.length; i += 2) {
    rows += `<tr>${cell(list[i])}${list[i + 1] ? cell(list[i + 1]) : '<td width="50%"></td>'}</tr>`;
  }
  return sectionTitle('Próximos eventos', t) +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 8px;">${rows}</table>`;
};

const lineupBlock = (c, t) => {
  const list = (c.lineup || []).filter(a => a && a.name).slice(0, 8);
  if (!list.length) return '';
  const row = (a) => `<tr><td style="padding:6px 0;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
      <td width="48" valign="middle">${a.photo
        ? `<img src="${safeHtml(a.photo)}" alt="" width="44" height="44" style="width:44px;height:44px;border-radius:50%;object-fit:cover;display:block;border:2px solid ${t.accent};"/>`
        : `<div style="width:44px;height:44px;border-radius:50%;background:${t.blockBg};border:2px solid ${t.accent};text-align:center;line-height:44px;font-weight:900;color:${t.accent};font-size:16px;">${safeHtml((a.name || '?')[0].toUpperCase())}</div>`}
      </td>
      <td valign="middle" style="padding-left:12px;">
        <div style="font-size:14px;font-weight:800;color:${t.text};">${safeHtml(a.name)}</div>
        ${a.role ? `<div style="font-size:11px;color:${t.sub};">${safeHtml(a.role)}</div>` : ''}
      </td>
    </tr></table>
  </td></tr>`;
  return sectionTitle('Line Up', t) +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 8px;">${list.map(row).join('')}</table>`;
};

const discountBlock = (c, t) => {
  if (!c.discount_code) return '';
  return `
  <div style="margin:24px 0;border:2px dashed ${t.accent};border-radius:14px;padding:22px;text-align:center;background:${t.blockBg};">
    <div style="font-size:10px;letter-spacing:3px;text-transform:uppercase;color:${t.sub};margin-bottom:8px;">Tu código de descuento</div>
    <div style="font-size:32px;font-weight:900;letter-spacing:5px;color:${t.accent};font-family:'Courier New',monospace;">${safeHtml(c.discount_code)}</div>
    ${c.discount_note ? `<div style="font-size:13px;color:${t.text};font-weight:600;margin-top:8px;">${safeHtml(c.discount_note)}</div>` : ''}
  </div>`;
};

const ctaBlock = (c, t) => {
  const primary = c.cta_text
    ? `<a href="${safeHtml(c.cta_url || '#')}" style="display:inline-block;padding:16px 36px;background:${t.accent};color:${textOn(t.accent)};text-decoration:none;border-radius:999px;font-weight:900;font-size:13px;letter-spacing:2px;text-transform:uppercase;">${safeHtml(c.cta_text)}</a>`
    : '';
  const secondary = c.cta2_text
    ? `<a href="${safeHtml(c.cta2_url || '#')}" style="display:inline-block;padding:13px 30px;background:transparent;color:${t.accent};border:2px solid ${t.accent};text-decoration:none;border-radius:999px;font-weight:800;font-size:12px;letter-spacing:2px;text-transform:uppercase;">${safeHtml(c.cta2_text)}</a>`
    : '';
  if (!primary && !secondary) return '';
  return `<div style="text-align:center;margin:28px 0 8px;">${primary}${primary && secondary ? '<div style="height:12px;line-height:12px;font-size:0;">&nbsp;</div>' : ''}${secondary}</div>`;
};

const socialsBlock = (c, t) => {
  const s = c.socials || {};
  const links = [
    ['Instagram', s.instagram],
    ['Spotify', s.spotify],
    ['TikTok', s.tiktok],
    ['Web', s.website],
  ].filter(([, v]) => v && String(v).trim());
  if (!links.length) return '';
  const pills = links.map(([label, url]) =>
    `<a href="${safeHtml(normalizeUrl(url))}" style="display:inline-block;margin:4px;padding:8px 18px;border:1px solid ${t.cardBorder};border-radius:999px;color:${t.sub};font-size:11px;letter-spacing:1px;text-transform:uppercase;text-decoration:none;font-weight:700;">${label}</a>`
  ).join('');
  return `<div style="text-align:center;margin:28px 0 4px;padding-top:20px;border-top:1px solid ${t.cardBorder};">${pills}</div>`;
};

const footerBlock = (t) => `
  <tr><td style="padding:24px 32px;border-top:1px solid ${t.cardBorder};text-align:center;color:${t.faint};font-size:11px;line-height:1.5;">
    Recibís este email porque comprás entradas con nosotros.<br/>
    <a href="{{unsubscribe_url}}" style="color:${t.accent};text-decoration:none;">Darme de baja</a>
  </td></tr>`;

const assemble = (c, t, hero, opts = {}) => {
  const contentSection = `<tr><td style="padding:32px;">
    ${bodyBlock(c, t)}
    ${countdownBlock(c, t)}
    ${eventsGridBlock(c, t)}
    ${lineupBlock(c, t)}
    ${discountBlock(c, t)}
    ${ctaBlock(c, t)}
    ${socialsBlock(c, t)}
  </td></tr>`;
  return `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${t.bodyBg};font-family:${t.font};color:${t.text};">
  <tr><td align="center" style="padding:40px 16px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background:${t.cardBg};border-radius:${t.radius};overflow:hidden;border:1px solid ${t.cardBorder};">
      ${logoBlock(c, t)}
      ${opts.noBanner ? '' : bannerBlock(c)}
      ${hero(c, t)}
      ${contentSection}
      <tr><td style="padding:0;"><!--DISCOUNT_BLOCK--></td></tr>
      ${footerBlock(t)}
    </table>
  </td></tr>
</table>`.trim();
};

// ---------------------------------------------------------------------------
// Heroes (template-specific top sections)
// ---------------------------------------------------------------------------

const badgePill = (text, t, extra = '') =>
  `<div style="display:inline-block;padding:6px 14px;border:1px solid ${t.accent};border-radius:999px;color:${t.accent};font-size:10px;letter-spacing:3px;text-transform:uppercase;margin-bottom:20px;${extra}">${safeHtml(text)}</div>`;

const heroLuxury = (c, t) => `
  <tr><td style="padding:48px 32px 24px;text-align:center;background:linear-gradient(135deg,#1a1308 0%,#2d1f0c 100%);">
    ${badgePill(c.badge || 'Evento Premium', t)}
    <h1 style="margin:0 0 12px;font-size:42px;line-height:1.05;letter-spacing:-1px;color:${t.accent};font-weight:900;text-transform:uppercase;">${safeHtml(c.title || 'Tu evento')}</h1>
    ${c.subtitle ? `<p style="margin:0;color:${t.sub};font-size:15px;letter-spacing:1px;">${safeHtml(c.subtitle)}</p>` : ''}
  </td></tr>`;

const heroUrgent = (c, t) => `
  <tr><td style="padding:48px 32px;text-align:center;background:linear-gradient(135deg,#2d0a0a 0%,#4a0a1a 100%);">
    ${badgePill(c.badge || 'Últimas entradas', t)}
    <h1 style="margin:0 0 14px;font-size:36px;line-height:1.1;color:#fff;font-weight:900;">${safeHtml(c.title || 'No te quedes afuera')}</h1>
    ${c.subtitle ? `<p style="margin:0;color:${t.sub};font-size:14px;">${safeHtml(c.subtitle)}</p>` : ''}
  </td></tr>`;

const heroNewsletter = (c, t) => `
  <tr><td style="padding:40px 32px 24px;background:linear-gradient(135deg,#0F0F1E 0%,#1E1B3A 100%);">
    <h1 style="margin:0 0 8px;font-size:28px;color:#fff;font-weight:800;letter-spacing:-0.5px;">${safeHtml(c.title || 'Novedades del mes')}</h1>
    ${c.subtitle ? `<p style="margin:0;color:${t.sub};font-size:13px;">${safeHtml(c.subtitle)}</p>` : ''}
  </td></tr>`;

const heroThanks = (c, t) => `
  <tr><td style="padding:56px 32px 32px;text-align:center;background:linear-gradient(135deg,#1a2e1a 0%,#14302c 100%);">
    <div style="font-size:48px;margin-bottom:16px;">✨</div>
    <h1 style="margin:0 0 12px;font-size:32px;color:#fff;font-weight:800;">${safeHtml(c.title || '¡Gracias por venir!')}</h1>
    ${c.subtitle ? `<p style="margin:0;color:${t.sub};font-size:14px;">${safeHtml(c.subtitle)}</p>` : ''}
  </td></tr>`;

const heroMinimal = (c, t) => `
  <tr><td style="padding:44px 32px 0;">
    <h1 style="margin:0;font-size:32px;color:${t.text};font-weight:300;letter-spacing:-0.5px;line-height:1.2;">${safeHtml(c.title || 'Tu mensaje principal')}</h1>
    ${c.subtitle ? `<p style="margin:10px 0 0;color:${t.sub};font-size:14px;">${safeHtml(c.subtitle)}</p>` : ''}
  </td></tr>`;

const heroGlass = (c, t) => `
  <tr><td style="padding:20px 20px 0;">
    <div style="background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.14);border-radius:16px;padding:44px 28px;text-align:center;">
      ${badgePill(c.badge || 'Próximamente', t)}
      <h1 style="margin:0 0 12px;font-size:38px;line-height:1.05;color:#fff;font-weight:900;letter-spacing:-1px;">${safeHtml(c.title || 'Tu evento')}</h1>
      ${c.subtitle ? `<p style="margin:0;color:${t.sub};font-size:14px;letter-spacing:1px;">${safeHtml(c.subtitle)}</p>` : ''}
    </div>
  </td></tr>`;

const heroVaporwave = (c, t) => `
  <tr><td style="padding:0;height:8px;background:linear-gradient(90deg,#FF6EC7 0%,#8B5CF6 50%,#00F0FF 100%);font-size:0;line-height:8px;">&nbsp;</td></tr>
  <tr><td style="padding:48px 32px 28px;text-align:center;background:linear-gradient(160deg,#1B0B2E 0%,#2A0A3E 100%);">
    <div style="display:inline-block;padding:6px 14px;border:1px solid #00F0FF;border-radius:999px;color:#00F0FF;font-size:10px;letter-spacing:4px;text-transform:uppercase;margin-bottom:20px;">${safeHtml(c.badge || 'New Wave')}</div>
    <h1 style="margin:0 0 8px;font-size:40px;line-height:1.05;color:${t.accent};font-weight:900;font-style:italic;text-transform:uppercase;letter-spacing:2px;">${safeHtml(c.title || 'Retro Night')}</h1>
    ${c.subtitle ? `<p style="margin:0;color:#00F0FF;font-size:13px;letter-spacing:3px;text-transform:uppercase;">${safeHtml(c.subtitle)}</p>` : ''}
  </td></tr>`;

const heroEditorial = (c, t) => `
  <tr><td style="padding:44px 40px 8px;">
    <div style="font-size:11px;letter-spacing:4px;text-transform:uppercase;color:${t.accent};font-weight:700;border-bottom:3px double #1a1a1a;padding-bottom:10px;margin-bottom:20px;">${safeHtml(c.badge || 'Edición Especial')}</div>
    <h1 style="margin:0 0 12px;font-size:40px;line-height:1.08;color:#111;font-weight:700;font-family:Georgia,'Times New Roman',serif;">${safeHtml(c.title || 'La nota de la semana')}</h1>
    ${c.subtitle ? `<p style="margin:0 0 8px;color:${t.sub};font-size:15px;font-style:italic;font-family:Georgia,serif;">${safeHtml(c.subtitle)}</p>` : ''}
    <div style="border-top:1px solid #d6d0c2;margin-top:16px;">&nbsp;</div>
  </td></tr>`;

const heroTicket = (c, t) => `
  <tr><td style="padding:36px 32px 24px;text-align:center;border-bottom:2px dashed rgba(250,204,21,0.5);">
    <div style="display:inline-block;padding:5px 16px;background:${t.accent};color:#000;border-radius:4px;font-size:10px;letter-spacing:4px;text-transform:uppercase;font-weight:900;margin-bottom:18px;">${safeHtml(c.badge || 'Admit One')}</div>
    <h1 style="margin:0 0 10px;font-size:36px;line-height:1.05;color:#fff;font-weight:900;text-transform:uppercase;letter-spacing:1px;">${safeHtml(c.title || 'Tu entrada te espera')}</h1>
    ${c.subtitle ? `<p style="margin:0;color:${t.sub};font-size:13px;letter-spacing:2px;text-transform:uppercase;">${safeHtml(c.subtitle)}</p>` : ''}
    <div style="margin-top:18px;font-family:'Courier New',monospace;font-size:11px;letter-spacing:6px;color:${t.accent};">★ BRAVO ★ VALID ★ BRAVO ★</div>
  </td></tr>`;

const heroImpact = (c, t) => {
  const img = c.banner_image || c.event_image;
  const bgStyle = img
    ? `background:#111 url('${safeHtml(img)}') center / cover no-repeat;`
    : 'background:linear-gradient(135deg,#111 0%,#333 100%);';
  return `
  <tr><td background="${img ? safeHtml(img) : ''}" style="${bgStyle}padding:0;">
    <div style="background:rgba(0,0,0,0.55);padding:90px 32px;text-align:center;">
      ${c.badge ? `<div style="display:inline-block;padding:6px 14px;border:1px solid rgba(255,255,255,0.5);border-radius:999px;color:#fff;font-size:10px;letter-spacing:3px;text-transform:uppercase;margin-bottom:20px;">${safeHtml(c.badge)}</div>` : ''}
      <h1 style="margin:0 0 12px;font-size:48px;line-height:1;color:#fff;font-weight:900;text-transform:uppercase;letter-spacing:-1px;">${safeHtml(c.title || 'Tu evento')}</h1>
      ${c.subtitle ? `<p style="margin:0;color:rgba(255,255,255,0.85);font-size:16px;letter-spacing:2px;">${safeHtml(c.subtitle)}</p>` : ''}
    </div>
  </td></tr>`;
};

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

const darkTheme = (over) => ({
  mode: 'dark',
  font: "'Helvetica Neue',Arial,sans-serif",
  radius: '16px',
  text: 'rgba(255,255,255,0.88)',
  sub: 'rgba(255,255,255,0.65)',
  faint: 'rgba(255,255,255,0.4)',
  ...over,
});

export const EMAIL_TEMPLATES = [
  {
    id: 'luxury_event',
    label: 'Evento Premium',
    description: 'Dorado / luxury para fiestas exclusivas',
    swatch: 'linear-gradient(135deg, #1a1308 0%, #2d1f0c 100%)',
    theme: darkTheme({ bodyBg: '#0A0805', cardBg: '#0A0805', cardBorder: 'rgba(212,175,55,0.2)', blockBg: 'rgba(212,175,55,0.06)', accent: '#D4AF37' }),
    hero: heroLuxury,
    defaults: {
      badge: 'Evento Premium',
      title: 'Catamarán Sunset Party',
      subtitle: '15 de Marzo · 22:00 hs',
      body1: 'Hola,\n\nTe queremos invitar a una experiencia única — música, atardecer y la mejor energía sobre el Río de la Plata.',
      body2: 'Las entradas son limitadas, asegurá la tuya antes que se agoten.',
      cta_text: 'Comprar entradas',
      cta_url: 'https://bravoproductora.com/eventos',
    },
  },
  {
    id: 'urgent_lastcall',
    label: 'Última Oportunidad',
    description: 'Tonos cálidos para sold-out inminente',
    swatch: 'linear-gradient(135deg, #2d0a0a 0%, #4a0a1a 100%)',
    theme: darkTheme({ bodyBg: '#0E0606', cardBg: '#0E0606', cardBorder: 'rgba(248,113,113,0.2)', blockBg: 'rgba(248,113,113,0.06)', accent: '#F87171' }),
    hero: heroUrgent,
    defaults: {
      badge: 'Últimas entradas',
      title: 'No te quedes afuera',
      subtitle: 'Quedan menos de 20 lugares',
      body1: 'Las entradas para nuestra próxima fiesta están casi agotadas.',
      body2: 'Si pensabas venir, este es el momento. Después del sold-out no podemos liberar más cupos.',
      cta_text: 'Asegurar mi lugar',
      cta_url: 'https://bravoproductora.com/eventos',
    },
  },
  {
    id: 'newsletter',
    label: 'Newsletter',
    description: 'Multi-sección para varios anuncios',
    swatch: 'linear-gradient(135deg, #0F0F1E 0%, #1E1B3A 100%)',
    theme: darkTheme({ bodyBg: '#080812', cardBg: '#080812', cardBorder: 'rgba(139,92,246,0.2)', blockBg: 'rgba(139,92,246,0.06)', accent: '#8B5CF6' }),
    hero: heroNewsletter,
    defaults: {
      title: 'Novedades del mes',
      subtitle: 'Marzo 2026',
      body1: 'Próximos eventos: Catamarán Sunset, Open Air en Costanera, Closing Party — fechas y reservas en nuestra web.',
      body2: 'Nuevos DJs: sumamos a nuestro roster artistas internacionales. Conocelos en la sección de comunidad.',
      cta_text: 'Ver todos los eventos',
      cta_url: 'https://bravoproductora.com/eventos',
    },
  },
  {
    id: 'thank_you',
    label: 'Agradecimiento',
    description: 'Post-evento con código de descuento',
    swatch: 'linear-gradient(135deg, #1a2e1a 0%, #14302c 100%)',
    theme: darkTheme({ bodyBg: '#06120C', cardBg: '#06120C', cardBorder: 'rgba(52,211,153,0.2)', blockBg: 'rgba(52,211,153,0.06)', accent: '#34D399' }),
    hero: heroThanks,
    defaults: {
      title: '¡Gracias por venir!',
      subtitle: 'Te queremos volver a ver',
      body1: 'Queremos agradecerte por ser parte de nuestro último evento. Tu energía hizo que la noche sea inolvidable.',
      body2: 'Como agradecimiento, te dejamos un código de descuento para tu próxima entrada: GRACIAS10',
      cta_text: 'Ver próximos eventos',
      cta_url: 'https://bravoproductora.com/eventos',
    },
  },
  {
    id: 'minimal',
    label: 'Minimalista',
    description: 'Blanco / negro, foco en el mensaje',
    swatch: 'linear-gradient(135deg, #f5f5f4 0%, #e7e5e4 100%)',
    theme: {
      mode: 'light',
      font: "'Helvetica Neue',Arial,sans-serif",
      radius: '8px',
      bodyBg: '#f5f5f4', cardBg: '#ffffff', cardBorder: '#e7e5e4', blockBg: '#fafaf9',
      text: '#27272a', sub: '#52525B', faint: '#A8A29E', accent: '#000000',
    },
    hero: heroMinimal,
    defaults: {
      title: 'Tu mensaje principal',
      body1: 'Hola, te quería contar algo importante.',
      body2: 'Contá la novedad acá. Mantenelo corto, claro y conversacional.',
      cta_text: 'Saber más',
      cta_url: 'https://bravoproductora.com',
    },
  },
  {
    id: 'glass',
    label: 'Glassmorphism',
    description: 'Paneles de vidrio sobre azul profundo',
    swatch: 'linear-gradient(160deg, #0B1023 0%, #1B1240 100%)',
    theme: darkTheme({ bodyBg: '#0B1023', cardBg: 'linear-gradient(160deg,#0E1430 0%,#1B1240 100%)', cardBorder: 'rgba(125,211,252,0.25)', blockBg: 'rgba(255,255,255,0.06)', accent: '#7DD3FC', radius: '20px' }),
    hero: heroGlass,
    defaults: {
      badge: 'Próximamente',
      title: 'Midnight Sessions',
      subtitle: 'Una noche que no vas a olvidar',
      body1: 'Sonido envolvente, visuales inmersivos y una selección musical curada al detalle.',
      body2: 'Cupos limitados. Entrada anticipada con beneficio exclusivo.',
      cta_text: 'Reservar lugar',
      cta_url: 'https://bravoproductora.com/eventos',
    },
  },
  {
    id: 'vaporwave',
    label: 'Retro Vaporwave',
    description: 'Rosa neón + cian ochentoso',
    swatch: 'linear-gradient(90deg, #FF6EC7 0%, #8B5CF6 50%, #00F0FF 100%)',
    theme: darkTheme({ bodyBg: '#14041F', cardBg: '#1B0B2E', cardBorder: 'rgba(255,110,199,0.35)', blockBg: 'rgba(0,240,255,0.06)', accent: '#FF6EC7' }),
    hero: heroVaporwave,
    defaults: {
      badge: 'New Wave',
      title: 'Retro Night',
      subtitle: 'Synth · Funk · Disco',
      body1: 'Viajá en el tiempo con nosotros: una fiesta con estética retro, neones y los mejores clásicos remixados.',
      body2: 'Dress code: brillos, colores y actitud.',
      cta_text: 'Quiero mi entrada',
      cta_url: 'https://bravoproductora.com/eventos',
    },
  },
  {
    id: 'editorial',
    label: 'Editorial Revista',
    description: 'Serif elegante estilo magazine',
    swatch: 'linear-gradient(135deg, #F4EFE6 0%, #E8E0CE 100%)',
    theme: {
      mode: 'light',
      font: "Georgia,'Times New Roman',serif",
      radius: '4px',
      bodyBg: '#F4EFE6', cardBg: '#FFFDF8', cardBorder: '#d6d0c2', blockBg: '#F7F2E7',
      text: '#2b2a26', sub: '#6b6555', faint: '#a39d8c', accent: '#C8102E',
    },
    hero: heroEditorial,
    defaults: {
      badge: 'Edición Especial',
      title: 'La temporada que se viene',
      subtitle: 'Todo lo que tenés que saber antes que nadie',
      body1: 'Esta temporada preparamos una curaduría de eventos que combina música, arte y experiencias gastronómicas.',
      body2: 'Te adelantamos las fechas clave para que agendes con tiempo.',
      cta_text: 'Leer la agenda completa',
      cta_url: 'https://bravoproductora.com/eventos',
    },
  },
  {
    id: 'ticket3d',
    label: 'Ticket 3D',
    description: 'Estilo entrada troquelada',
    swatch: 'linear-gradient(135deg, #0A0A0F 0%, #1c1c24 100%)',
    theme: darkTheme({ bodyBg: '#0A0A0F', cardBg: '#111118', cardBorder: 'rgba(250,204,21,0.35)', blockBg: 'rgba(250,204,21,0.06)', accent: '#FACC15', radius: '18px' }),
    hero: heroTicket,
    defaults: {
      badge: 'Admit One',
      title: 'Tu entrada te espera',
      subtitle: 'Golden Ticket Experience',
      body1: 'Conseguiste acceso prioritario a la venta de entradas de nuestro próximo evento.',
      body2: 'Este beneficio es por tiempo limitado — no lo dejes pasar.',
      cta_text: 'Canjear acceso',
      cta_url: 'https://bravoproductora.com/eventos',
    },
  },
  {
    id: 'hero_impact',
    label: 'Hero Impact',
    description: 'Imagen full-bleed con texto encima',
    swatch: 'linear-gradient(135deg, #111 0%, #444 100%)',
    theme: darkTheme({ bodyBg: '#0c0c0c', cardBg: '#141414', cardBorder: 'rgba(255,255,255,0.12)', blockBg: 'rgba(255,255,255,0.05)', accent: '#FFFFFF' }),
    hero: heroImpact,
    noBanner: true,
    defaults: {
      badge: 'Anuncio',
      title: 'Main Event',
      subtitle: 'La fecha más esperada del año',
      body1: 'Prepárate para una producción sin precedentes: escenario 360, sonido de primer nivel y un lineup de lujo.',
      body2: 'Preventa exclusiva por tiempo limitado.',
      cta_text: 'Comprar ahora',
      cta_url: 'https://bravoproductora.com/eventos',
    },
  },
];

export const getEmailTemplate = (id) => EMAIL_TEMPLATES.find(t => t.id === id);

/**
 * Render the final HTML given a template id and content.
 * `content.accent_override` replaces the theme accent everywhere.
 */
export const renderEmailHtml = (templateId, content) => {
  const tpl = getEmailTemplate(templateId);
  if (!tpl) return '';
  const c = content || {};
  const theme = { ...tpl.theme };
  if (c.accent_override) {
    theme.accent = String(c.accent_override).replace(/[<>'"]/g, '');
  }
  return assemble(c, theme, tpl.hero, { noBanner: !!tpl.noBanner });
};

/**
 * Fields shown to the producer per template — shared simple schema.
 */
export const TEMPLATE_FIELDS = [
  { key: 'badge',     label: 'Etiqueta superior (opcional)', placeholder: 'Ej: Evento Premium',            type: 'text' },
  { key: 'title',     label: 'Título principal',   placeholder: 'Ej: Catamarán Sunset Party',        type: 'text' },
  { key: 'subtitle',  label: 'Subtítulo / fecha',  placeholder: 'Ej: 15 de Marzo · 22 hs',           type: 'text' },
  { key: 'body1',     label: 'Mensaje principal',  placeholder: 'Escribí el cuerpo del mail acá…',   type: 'textarea' },
  { key: 'body2',     label: 'Mensaje secundario (opcional)', placeholder: 'Detalle, descuento o aviso adicional', type: 'textarea' },
  { key: 'cta_text',  label: 'Texto del botón',    placeholder: 'Ej: Comprar entradas',              type: 'text' },
  { key: 'cta_url',   label: 'Link del botón',     placeholder: 'https://…',                         type: 'text' },
  { key: 'cta2_text', label: 'Botón secundario (opcional)', placeholder: 'Ej: Ver lineup completo',  type: 'text' },
  { key: 'cta2_url',  label: 'Link botón secundario', placeholder: 'https://…',                      type: 'text' },
];
